import { ColumnDef, Row } from "@tanstack/react-table";
import Icon from "@/components/Icon";
import { formatSourceName } from "@/features/global/components/NotesTable/noteHelpers";
import { SortFieldType } from "./sortUtils";

interface BuildSourceColumnProps<T> {
  accessorKey: string;
  header: string;
  getParentLink: (row: T) => string | null | undefined;
  getParentType: (row: T) => string | null | undefined;
  accessorFn?: (row: T) => unknown;
  size?: number;
  minSize?: number;
  sortType?: SortFieldType;
  enableSorting?: boolean;
  meta?: Record<string, any>;
  navigate?: (to: string) => void;
  currentPathname?: string;
}

/**
 * Helper to create a column that displays a clickable source link with navigation icon.
 * The link navigates to the parent record, and clicking it stops event propagation
 * so the row click handler doesn't fire.
 *
 * @example
 * buildSourceColumn({
 *   accessorKey: "source",
 *   header: "Source",
 *   getParentLink: (note) => note.parentId ? `/people/${note.pgaId}/membership` : null,
 *   getParentType: (note) => note.parentType,
 *   size: 150
 * })
 */
export function buildSourceColumn<T>({
  accessorKey,
  header,
  getParentLink,
  getParentType,
  accessorFn,
  size,
  minSize,
  sortType,
  enableSorting,
  meta,
  navigate,
  currentPathname
}: BuildSourceColumnProps<T>): ColumnDef<T> {
  const CellComponent = ({ row }: { row: Row<T> }) => {
    const parentLink = getParentLink(row.original);
    const parentType = getParentType(row.original);

    if (!parentType) {
      return <span className="opacity-20">--</span>;
    }

    const formattedType = formatSourceName(parentType);
    const linkPathname = parentLink?.split("?")[0];
    const isCurrentPage = linkPathname && currentPathname === linkPathname;

    if (!parentLink || isCurrentPage || !navigate) {
      return <span>{formattedType}</span>;
    }

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(parentLink);
    };

    return (
      <div data-source-link="true" onClick={handleClick} className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={handleClick}
          className="p-0 m-0 bg-transparent border-0 text-left underline text-sys-brand-on-surface hover:text-sys-brand-on-surface-variant cursor-pointer"
        >
          {formattedType}
        </button>
        <Icon name="navigate_next" size={20} className="text-sys-brand-on-surface-variant flex-shrink-0" />
      </div>
    );
  };

  return {
    ...(accessorFn ? { id: accessorKey, accessorFn } : { accessorKey }),
    header,
    ...(size && { size }),
    ...(minSize && { minSize }),
    ...(enableSorting !== undefined && { enableSorting }),
    meta: {
      ...(sortType && { sortType }),
      ...meta
    },
    cell: CellComponent
  };
}
